import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { ReactElement, Suspense, useState } from 'react';
import {
  GQL_MUTATION_UPDATE_SOURCE,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_QUERY_READ_INTEGRATION,
  GQL_QUERY_READ_SOURCE,
} from '../utility/queries';
import {
  IntegrationType,
  SourceType,
  UserCompanyLimitation,
} from '@sc/graphql';
import {
  Button,
  FullLoader,
  Input,
  transformIntegrationPath,
} from '@sc/shared-ui';
import { integrationOptions } from '../utility/integrations';
import { ConnectIntegration } from '../components/ConnectIntegration';
import { useForm } from '@tanstack/react-form';
import {
  DocumentIcon,
  FolderIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { notify } from '../utility/notify';
import { isThirdPartyCookieRestrictedBrowser } from '../utility/browser';
import { useAlerts } from '../providers/alerts';
import { GoogleDrivePicker } from './GoogleDrivePicker';
import { SourceDocumentCard } from '../components/SourceDocumentCard';
import { useUser } from '../providers/users';
import { track } from '../utility/analytics';

const GoogleDriveSource = ({ projectUuid }: { projectUuid: string }) => {
  const { data: integration } = useSuspenseQuery(GQL_QUERY_READ_INTEGRATION, {
    variables: {
      type: IntegrationType.googleDrive,
    },
  });

  const { data: source } = useSuspenseQuery(GQL_QUERY_READ_SOURCE, {
    variables: {
      input: {
        type: SourceType.googleDrive,
        projectUuid,
      },
    },
  });

  const { data: sourceDocuments } = useSuspenseQuery(
    GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
    {
      variables: {
        dimensionsInput: undefined,
        input: {
          filter: {
            sourceUuid: source.readSource.uuid,
          },
          pagination: {
            perPage: 20,
            page: 1,
          },
        },
      },
    }
  );

  const { useApolloError, setAlertModal } = useAlerts();
  const onError = useApolloError();

  const [updateSource] = useMutation(GQL_MUTATION_UPDATE_SOURCE, {
    onError,
    onCompleted: () => notify.success('Saved source name.'),
  });

  const form = useForm({
    defaultValues: {
      name: source.readSource.name ?? '',
    },
    onSubmit: ({ value }) => {
      if (value.name && value.name !== source.readSource.name) {
        void updateSource({
          variables: {
            input: {
              uuid: source.readSource.uuid,
              name: value.name,
            },
          },
        });
      }
    },
  });

  const [isAddingFiles, setIsAddingFiles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { user } = useUser();

  if (!integration.readIntegration) {
    return (
      <ConnectIntegration
        integration={{
          ...integrationOptions.googleDrive,
          oAuthUrl: integrationOptions.googleDrive.oAuthUrl(projectUuid),
        }}
        onConnect={() =>
          track('Integration Connect Clicked', {
            integrationType: IntegrationType.googleDrive,
            origin: 'project-sources',
          })
        }
      />
    );
  }

  return (
    <>
      <div className="overflow-y-auto flex flex-col h-full w-full">
        <div className="p-5 space-y-5">
          <form.Field
            name="name"
            children={({ state, handleChange }) => (
              <Input
                label="Source name (optional)"
                onBlur={() => void form.handleSubmit()}
                value={state.value}
                inputClassname="h-10!"
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
          <Button
            button={{
              text: 'Select files',
              disabled: user.company.limitations.includes(
                UserCompanyLimitation.cannotCreateQueryMessage
              ),
              loading: isSaving,
              type: 'secondary',
              IconEnd: PlusIcon,
              onClick: () => {
                if (isThirdPartyCookieRestrictedBrowser()) {
                  setAlertModal({
                    open: true,
                    icon: 'warning',
                    title: 'Google Drive works best in Chrome',
                    description:
                      'Safari blocks the third-party cookies that Google Drive’s file picker needs, so it usually shows a “Can’t access your Google Account” error. To add files, open StarkChat in Google Chrome — or in Safari go to Settings ▸ Privacy and turn off “Prevent cross-site tracking”, then try again.',
                    submitText: 'Try anyway',
                    onSubmit: () => {
                      setAlertModal(undefined);

                      setIsAddingFiles(true);
                    },
                    closeText: 'Cancel',
                  });

                  return;
                }

                setIsAddingFiles(true);
              },
            }}
          />
        </div>
        <div className="px-5 space-y-5 pb-5">
          {sourceDocuments.indexSourceDocuments.items.map((sd) => (
            <SourceDocumentCard
              sd={sd}
              sourceType={source.readSource.type}
              sourceUuid={source.readSource.uuid}
              FallbackIcon={
                sd.hostedIntegrationDocumentId?.includes('folder')
                  ? FolderIcon
                  : DocumentIcon
              }
              key={sd.uuid}
              getPath={(sd) =>
                sd.hostedIntegrationPath &&
                transformIntegrationPath(sd.hostedIntegrationPath)
              }
            />
          ))}
        </div>
      </div>
      <GoogleDrivePicker
        open={isAddingFiles}
        onClose={() => setIsAddingFiles(false)}
        sourceUuid={source.readSource.uuid}
        projectUuid={projectUuid}
        onLoadingChange={setIsSaving}
      />
    </>
  );
};

export const GoogleDrive = ({
  projectUuid,
}: {
  projectUuid: string;
}): ReactElement => (
  <Suspense fallback={<FullLoader className="size-5" />}>
    <GoogleDriveSource projectUuid={projectUuid} />
  </Suspense>
);
